import { useNavigate } from "react-router-dom";
import { useState, useMemo } from "react";

function formatDatePro(date) {
  if (!date) return "-";

  const d = new Date(date);

  const jours = String(d.getDate()).padStart(2, "0");

  const mois = [
    "Jan",
    "Fév",
    "Mar",
    "Avr",
    "Mai",
    "Juin",
    "Juil",
    "Août",
    "Sep",
    "Oct",
    "Nov",
    "Déc",
  ];

  return `${jours} ${mois[d.getMonth()]} ${d.getFullYear()}`;
}

  const money = (value) =>
    Number(value || 0).toLocaleString("fr-FR");

function HistoriqueModal({
  data = [],
  onClose,
}) {

  const navigate = useNavigate();

  const [search, setSearch] = useState("");

  const [filter, setFilter] = useState("tous");

  const [expandedId, setExpandedId] = useState(null);

  /* ===============================
      STATISTIQUES
  ================================ */

  const stats = useMemo(() => {

    let revenus = 0;

    let paye = 0;

    let restant = 0;

    let nuits = 0;

    const clients = new Set();

    data.forEach((s) => {

      const total = Number(s.total_sejour || 0);

      const totalPaye = Number(s.total_paye || 0);

      revenus += total;

      paye += totalPaye;

      restant += Math.max(0, total - totalPaye);

      if (s.nom || s.prenom) {

        clients.add(`${s.nom}-${s.prenom}`);

      }

      if (s.date_debut && s.date_fin_prevue) {

        const debut = new Date(s.date_debut);

        const fin = new Date(s.date_fin_prevue);

        nuits += Math.max(
          0,
          Math.round(
            (fin - debut) /
              (1000 * 60 * 60 * 24)
          )
        );

      }

    });

    return {

      revenus,

      paye,

      restant,

      nuits,

      clients: clients.size,

      tauxPaiement:
        revenus === 0
          ? 0
          : Math.round(
              (paye / revenus) * 100
            ),

    };

  }, [data]);

  /* ===============================
      RECHERCHE + FILTRE
  ================================ */

  const filteredData = useMemo(() => {

    return data.filter((s) => {

      const nom = `${s.nom ?? ""} ${s.prenom ?? ""}`.toLowerCase();

      const recherche =
        nom.includes(search.toLowerCase());

      const total = Number(s.total_sejour || 0);

      const paye = Number(s.total_paye || 0);

      const reste = Math.max(0, total - paye);

      let ok = true;

      switch (filter) {

        case "payes":

          ok = reste === 0;

          break;

        case "partiels":

          ok =
            paye > 0 &&
            reste > 0;

          break;

        case "impayes":

          ok =
            paye === 0;

          break;

        case "termines":

          ok =
            s.statut === "termine";

          break;

        case "annules":
          ok = s.statut === "annule";
          break;

        case "encours":

          ok =
            s.statut === "en_cours";

          break;

        case "expires":

          ok =
            s.statut === "expire";

          break;

        default:

          ok = true;

      }

      return recherche && ok;

    });

  }, [data, search, filter]);

  return (

  <div className="w-[92vw] max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl flex flex-col h-[90vh] max-h-[900px] overflow-hidden">

    {/* ================= HEADER ================= */}

    <div className="sticky top-0 bg-white border-b z-20 p-5 flex justify-between items-center">

      <div>

        <h2 className="text-xl font-bold">
          📜 Historique des séjours
        </h2>

        <p className="text-gray-500 text-sm">
          Historique complet de l'appartement
        </p>

      </div>

      <button

        onClick={onClose}

        className="px-5 py-2 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 transition"

      >
        Fermer
      </button>

    </div>


    {/* ================= DASHBOARD ================= */}

    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 px-4 py-3 bg-gray-50 border-b">

      <div className="bg-white rounded-lg border border-gray-100 px-3 py-2">

        <p className="text-[11px] uppercase tracking-wide text-gray-500">
          Séjours
        </p>

        <p className="text-lg font-semibold mt-1">
          {data.length}
        </p>

      </div>

      <div className="bg-white rounded-lg border border-gray-100 px-3 py-2">

        <p className="text-[11px] uppercase tracking-wide text-gray-500">
          Clients
        </p>

        <p className="text-lg font-semibold mt-1">
          {stats.clients}
        </p>

      </div>

      <div className="bg-white rounded-lg border border-gray-100 px-3 py-2">

        <p className="text-xs text-gray-500">
          Nuits
        </p>

        <p className="text-xl font-semibold">
          {stats.nuits}
        </p>

      </div>

      <div className="bg-white rounded-lg border border-gray-100 px-3 py-2">

        <p className="text-xs text-gray-500">
          Revenus
        </p>

        <p className="text-lg font-semibold mt-1">
          {money(stats.revenus)} $
        </p>

      </div>

      <div className="bg-white rounded-lg border border-gray-100 px-3 py-2">

        <p className="text-xs text-gray-500">
          Restant
        </p>

        <p className="text-lg font-semibold mt-1">
          {money(stats.restant)} $
        </p>

      </div>

      <div className="bg-white rounded-lg border border-gray-100 px-3 py-2">

        <p className="text-xs text-gray-500">
          Paiement
        </p>

        <p className="text-lg font-semibold mt-1">
          {stats.tauxPaiement} %
        </p>

      </div>

    </div>


    {/* ================= RECHERCHE ================= */}

    <div className="border-b p-4">

      <input

        type="text"

        value={search}

        onChange={(e)=>setSearch(e.target.value)}

        placeholder="🔎 Rechercher un client..."

        className="w-full rounded-xl border px-4 py-3 outline-none focus:ring-2 focus:ring-blue-500"

      />

    </div>


    {/* ================= FILTRES ================= */}

    <div className="flex gap-2 overflow-x-auto p-4 border-b">

      {[
        [
          ["tous", "Tous"],
          ["encours", "En cours"],
          ["termines", "Terminés"],
          ["annules", "Annulés"],
          ["expires", "Expirés"],
          ["payes", "Payés"],
          ["partiels", "Partiels"],
          ["impayes", "Impayés"]
        ]
      ].map(([value,label])=>(

        <button

          key={value}

          onClick={()=>setFilter(value)}

          className={`px-4 py-2 rounded-full whitespace-nowrap transition

          ${filter===value

            ? "bg-blue-600 text-white"

            : "bg-gray-100 hover:bg-gray-200"

          }`}

        >

          {label}

        </button>

      ))}

    </div>


    {/* ================= LISTE ================= */}

    <div className="flex-1 overflow-y-auto px-4 py-5">

      <div className="max-w-3xl mx-auto space-y-4">

        {filteredData.length===0 && (

          <div className="text-center py-10 text-gray-500">

            Aucun séjour trouvé.

          </div>

        )}

        {filteredData.map((s)=>{

          const total=Number(s.total_sejour||0);

          const paye=Number(s.total_paye||0);

          const reste=Math.max(0,total-paye);

          const ouvert=expandedId===s.id;

          
          let badge;

          if (s.statut === "annule") {
            badge = (
              <span className="px-3 py-1 rounded-full bg-red-100 text-red-700 text-xs font-semibold">
                Annulé
              </span>
            );
          } else if (s.statut === "termine") {
            badge = (
              <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-700 text-xs font-semibold">
                Terminé
              </span>
            );
          } else if (s.statut === "en_cours") {
            badge = (
              <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold">
                En cours
              </span>
            );
          } else if (s.statut === "confirme") {
            badge = (
              <span className="px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-semibold">
                Confirmé
              </span>
            );
          } else if (s.statut === "reserve") {
            badge = (
              <span className="px-3 py-1 rounded-full bg-yellow-100 text-yellow-700 text-xs font-semibold">
                Réservé
              </span>
            );
          } else if (s.statut === "expire") {
            badge = (
              <span className="px-3 py-1 rounded-full bg-gray-200 text-gray-700 text-xs font-semibold">
                Expiré
              </span>
            );
          } else if (s.statut === "no_show") {
            badge = (
              <span className="px-3 py-1 rounded-full bg-orange-100 text-orange-700 text-xs font-semibold">
                No-show
              </span>
            );
          } else if (reste === 0) {
            badge = (
              <span className="px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-semibold">
                Payé
              </span>
            );
          } else if (paye > 0) {
            badge = (
              <span className="px-3 py-1 rounded-full bg-yellow-100 text-yellow-700 text-xs font-semibold">
                Paiement partiel
              </span>
            );
          } else {
            badge = (
              <span className="px-3 py-1 rounded-full bg-red-100 text-red-700 text-xs font-semibold">
                Impayé
              </span>
            );
          }

          return (

            <div
              key={s.id}
              className="border border-gray-200 rounded-xl bg-white hover:shadow-md transition"
            >

              {/* En-tête */}

              <div className="px-4 pt-4 pb-3">

                <div className="flex items-start justify-between gap-4">

                  <div className="min-w-0">

                    <h3 className="text-base font-semibold text-gray-900 truncate">
                      {s.nom} {s.post_nom} {s.prenom}
                    </h3>

                    <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">

                      <span>
                        {formatDatePro(s.date_debut)}
                      </span>

                      <span className="text-gray-300">→</span>

                      <span>
                        {formatDatePro(s.date_fin_prevue)}
                      </span>

                    </div>

                  </div>

                  <div className="shrink-0">
                    {badge}
                  </div>

                </div>

                
                <div className="grid grid-cols-3 border-t border-b border-gray-100 mt-3">

                  <div className="py-3 pr-3">
                    <p className="text-[11px] uppercase tracking-wide text-gray-400">
                      Total
                    </p>

                    <p className="text-sm font-semibold text-gray-900 mt-1">
                      {money(total)} $
                    </p>
                  </div>

                  <div className="py-3 px-3 border-l border-gray-100">
                    <p className="text-[11px] uppercase tracking-wide text-gray-400">
                      Payé
                    </p>

                    <p className="text-sm font-semibold text-green-700 mt-1">
                      {money(paye)} $
                    </p>
                  </div>

                  <div className="py-3 pl-3 border-l border-gray-100">
                    <p className="text-[11px] uppercase tracking-wide text-gray-400">
                      {Number(s.trop_percu || 0) > 0
                        ? "Trop-perçu"
                        : "Reste"}
                    </p>

                    <p
                      className={`text-sm font-semibold mt-1 ${
                        Number(s.trop_percu || 0) > 0
                          ? "text-orange-600"
                          : "text-red-600"
                      }`}
                    >
                      {money(
                        Number(s.trop_percu || 0) > 0
                          ? s.trop_percu
                          : reste
                      )} $
                    </p>
                  </div>

                </div>


                {Number(s.trop_percu || 0) > 0 && (
                  <div className="mt-3 rounded-xl bg-orange-50 border border-orange-200 px-4 py-3">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-orange-700">
                          Trop-perçu
                        </p>

                        <p className="text-sm text-orange-800 mt-1">
                          Montant à rembourser au client
                        </p>
                      </div>

                      <div className="text-lg font-bold text-orange-700 whitespace-nowrap">
                        {money(s.trop_percu)} $
                      </div>
                    </div>

                    {s.trop_percu_rembourse && (
                      <p className="text-xs text-green-700 mt-2 font-medium">
                        ✓ Remboursement effectué
                      </p>
                    )}
                  </div>
                )}


                <div className="flex justify-end gap-2 mt-4">

                  <button

                    onClick={() =>
                      setExpandedId(
                        ouvert ? null : s.id
                      )
                    }

                    className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition"

                  >

                    {ouvert
                      ? "Voir moins"
                      : "Voir plus"}

                  </button>

                  <button

                    onClick={() => {

                      onClose();

                      navigate(`/sejours/${s.id}`);

                    }}

                    className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg border border-gray-200 transition"

                  >

                    Ouvrir le séjour

                  </button>

                </div>

                
                {ouvert && (

                  <div className="mt-4 border-t border-gray-100 pt-4">

                    <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">
                      Détails du séjour
                    </h4>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">

                      <div className="bg-gray-50 rounded-lg px-3 py-2">
                        <span className="text-xs text-gray-400">
                          Statut
                        </span>
                        <p className="text-sm font-medium text-gray-800 mt-1">
                          {s.statut || "-"}
                        </p>
                      </div>

                      <div className="bg-gray-50 rounded-lg px-3 py-2">
                        <span className="text-xs text-gray-400">
                          Source
                        </span>
                        <p className="text-sm font-medium text-gray-800 mt-1">
                          {s.source === "public" ? "Site" : "Interne"}
                        </p>
                      </div>

                      <div className="bg-gray-50 rounded-lg px-3 py-2">
                        <span className="text-xs text-gray-400">
                          Occupants
                        </span>
                        <p className="text-sm font-medium text-gray-800 mt-1">
                          {s.nombre_occupants ?? "-"}
                        </p>
                      </div>

                      <div className="bg-gray-50 rounded-lg px-3 py-2">
                        <span className="text-xs text-gray-400">
                          Trop-perçu
                        </span>
                        <p className="text-sm font-semibold text-orange-600 mt-1">
                          {money(s.trop_percu || 0)} $
                        </p>
                      </div>

                    </div>

                    <div className="grid grid-cols-2 gap-3 mt-3">

                      <div className="border border-gray-100 rounded-lg px-3 py-2">
                        <span className="text-xs text-gray-400">
                          Arrivée
                        </span>
                        <p className="text-sm font-medium text-gray-800 mt-1">
                          {formatDatePro(s.date_debut)}
                        </p>
                      </div>

                      <div className="border border-gray-100 rounded-lg px-3 py-2">
                        <span className="text-xs text-gray-400">
                          Départ
                        </span>
                        <p className="text-sm font-medium text-gray-800 mt-1">
                          {formatDatePro(s.date_fin_prevue)}
                        </p>
                      </div>

                    </div>

                    {Number(s.trop_percu || 0) > 0 && (
                      <div className="mt-3 rounded-lg bg-orange-50 border border-orange-200 px-3 py-2 flex items-center justify-between">

                        <div>
                          <p className="text-xs font-semibold text-orange-700">
                            Remboursement
                          </p>

                          <p className="text-xs text-orange-600 mt-1">
                            {s.trop_percu_rembourse
                              ? "Remboursement effectué"
                              : "Remboursement à effectuer"}
                          </p>
                        </div>

                        <span className="text-sm font-bold text-orange-700">
                          {money(s.trop_percu)} $
                        </span>

                      </div>
                    )}

                  </div>
                  
                )}


              </div>

            </div>

          );

        })}

      </div>

    </div>

  </div>

);

}

export default HistoriqueModal;